<div class="h-full overflow-y-auto bg-gray-50 dark:bg-gray-950">

  <!-- Step navigation (multi-step) -->
  @if (state.settings?.isMultiStep && (state.settings?.totalSteps ?? 0) > 1) {
    <div class="flex flex-wrap items-center gap-1 px-6 pt-5 max-w-2xl mx-auto">
      @for (step of stepsArray; track step) {
        <button
          (click)="builder.setActiveStep(step)"
          class="flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-medium transition-colors"
          [class.bg-brand-500]="state.activeStep === step"
          [class.text-white]="state.activeStep === step"
          [class.bg-gray-200]="state.activeStep !== step"
          [class.dark:bg-gray-800]="state.activeStep !== step"
          [class.text-gray-600]="state.activeStep !== step"
          [class.dark:text-gray-400]="state.activeStep !== step"
        >Paso {{ step }}</button>
      }
    </div>
  }

  <!-- Form card -->
  <div class="max-w-2xl mx-auto px-4 py-5 pb-10">
    <div class="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">

      <!-- Brand bar -->
      <div class="h-3 bg-brand-500"></div>

      <!-- Header: logo + title + description -->
      <div class="px-7 pt-6 pb-5 space-y-3">
        <!-- Logo -->
        <div>
          @if (logoUrl) {
            <div class="relative group w-fit">
              <img [src]="logoUrl" alt="Logo del negocio"
                (error)="onLogoLoadError()"
                class="h-12 w-auto max-w-[160px] object-contain rounded-lg" />
              <label class="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <span class="text-white text-xs font-medium px-2">
                  @if (logoUploading) { Subiendo... } @else { Cambiar logo }
                </span>
                <input type="file" accept="image/*" class="hidden"
                  (change)="onLogoSelected($event)" [disabled]="logoUploading" />
              </label>
            </div>
          } @else {
            <label class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 hover:border-brand-400 hover:bg-brand-50/50 dark:hover:bg-brand-500/5 transition-colors cursor-pointer group">
              @if (logoUploading) {
                <span class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500 flex-shrink-0"></span>
                <span class="text-sm text-gray-400">Subiendo...</span>
              } @else {
                <svg class="w-4 h-4 text-gray-400 group-hover:text-brand-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                <span class="text-sm text-gray-400 group-hover:text-brand-500">Añadir logo del negocio</span>
              }
              <input type="file" accept="image/*" class="hidden"
                (change)="onLogoSelected($event)" [disabled]="logoUploading" />
            </label>
          }
        </div>

        <!-- Título visible (inc25) -->
        <input
          type="text"
          [value]="formTitle"
          (blur)="onTitleBlur($event)"
          [placeholder]="formName || 'Título del formulario'"
          class="w-full text-3xl font-normal text-gray-900 dark:text-white bg-transparent border-b-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 focus:border-brand-500 dark:focus:border-brand-400 focus:outline-none pb-1.5 transition-colors placeholder:text-gray-300 dark:placeholder:text-gray-600"
        />
        <!-- Description -->
        <textarea
          [value]="formDescription"
          (blur)="onDescriptionBlur($event)"
          placeholder="Descripción del formulario (opcional)"
          rows="2"
          class="w-full text-sm text-gray-500 dark:text-gray-400 bg-transparent border-b border-gray-200 dark:border-gray-700 hover:border-gray-300 focus:border-brand-400 dark:focus:border-brand-400 focus:outline-none resize-none transition-colors placeholder:text-gray-300 dark:placeholder:text-gray-600 leading-relaxed"
        ></textarea>
      </div>

      <!-- Drop zone inside the card -->
      <div
        cdkDropList
        id="canvas-list"
        cdkDropListConnectedTo="palette-list"
        [cdkDropListData]="fields"
        (cdkDropListDropped)="onDrop($event)"
        class="px-7 pb-5 min-h-[120px] flex flex-col gap-3"
      >
        @if (fields.length === 0) {
          <div class="flex flex-col items-center justify-center min-h-[120px] rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/30 transition-colors">
            <svg class="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            <p class="text-sm font-medium text-gray-400 dark:text-gray-500">Arrastra campos aquí</p>
            <p class="text-xs text-gray-400 dark:text-gray-600 mt-0.5">o haz clic en un campo de la paleta</p>
          </div>
        }
        @for (field of fields; track field.fieldKey; let i = $index) {
          <app-canvas-field
            cdkDrag
            [cdkDragData]="field"
            [field]="field"
            [index]="i"
            [isSelected]="state.selectedFieldKey === field.fieldKey"
            (selectField)="onSelectField(field.fieldKey)"
            (removeField)="onRemoveField(field.fieldKey)"
            class="block"
          />
        }
      </div>

      <!-- Submit button preview -->
      <div class="px-7 pb-7 pt-1">
        <div class="h-px bg-gray-100 dark:bg-gray-800 mb-5"></div>
        <button type="button" disabled
          class="px-6 py-2.5 text-sm font-medium rounded-lg text-white bg-brand-500 opacity-70 cursor-default"
          [ngStyle]="submitButtonPreviewStyle">
          {{ submitLabel }}
        </button>
      </div>

    </div>
  </div>

</div>
